import {Component, OnInit} from '@angular/core';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import {buffer, debounceTime, filter, Observable} from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import {Auth} from "../../../shared/models/auth.model";
import {AuthService, SsoLoginFailedError} from "../../../shared/services/auth.service";
import {User} from "../../../shared/models/user.model";
import {ActivationEnd, NavigationEnd, Route, Router} from "@angular/router";
// Idle sign-out is handled by the backend: every API request keeps the SSO session alive.
// import {IdleService} from "../../../shared/services/idle-service";
import {TranslateService} from "../../../shared/services/translate.service";
import {SettingService} from "../../../shared/services/setting.service";
import {ToastrService} from "ngx-toastr";
import Swal from "sweetalert2";
import { NavigationItems } from './navigation';
import {SokratesSsoScriptService} from "../../../shared/services/sokrates-sso-script.service";

interface sidebarMenu {
  link: string;
  icon: string;
  menu: string;
  data: any;
  task?: string;
  children?: sidebarMenu[];
  expanded?: boolean;
}
@Component({
  selector: 'app-full',
  templateUrl: './full.component.html',
  styleUrls: ['./full.component.scss']
})
export class FullComponent implements OnInit {

  search: boolean = false;

  breadcrumbItems: any[] = [];

  webSettings: any;

  isHandset$: Observable<boolean> = this.breakpointObserver.observe(Breakpoints.Handset)
    .pipe(
      map(result => result.matches),
      shareReplay()
    );

  constructor(
      private breakpointObserver: BreakpointObserver,
      public auth: AuthService,
      private translate: TranslateService,
      public authService: AuthService,
      private settingService: SettingService,
      private router: Router,
      private toastr: ToastrService,
      private ssoScript: SokratesSsoScriptService,
    ) {
      this.webSettings = JSON.parse(this.settingService.getConfig('client.webSettings')?? '{}');
    }

  routerActive: string = "activelink";
  currentUser : User | null = null;
  canSwitchAccount: boolean = false;
  isSwitchedAccount: boolean = false;
  isSwitchingAccount: boolean = false;
  isRestoringAccount: boolean = false;
  realAdminName: string | null = null;
  academic = null;
  sidebarMenu: sidebarMenu[] = [
    {
      link: "/layout/dashboard",
      icon: "activity",
      menu: "Dashboard",
      data: { academic_year: this.academic },
    },
    {
      link: "/layout/school-program",
      icon: "layers",
      menu: "School Program",
      data: { academic_year:  this.academic },
      task: 'SCHOOL_PROGRAM_VIEW'
    },
    {
      link: "/layout/activity",
      icon: "dribbble",
      menu: "Activity",
      data: { academic_year:  this.academic },
      task: 'SCHOOL_PROGRAM_ACTIVITY_VIEW'
    },
    {
      link: "/layout/rule-settings",
      icon: "lock",
      menu: "Rules",
      task: 'SCHOOL_PROGRAM_RULE_VIEW',
      data: { academic_year: this.academic },
      expanded: false,
      children: [
        {
          link: "/layout/rule-settings/global-rules",
          icon: "lock",
          menu: "Global Rules",
          data: { academic_year:  this.academic },
          task: 'SCHOOL_PROGRAM_RULE_VIEW'
        },
        {
          link: "/layout/rule-settings/rules",
          icon: "lock",
          menu: "Year Level Rules",
          data: { academic_year:  this.academic },
          task: 'SCHOOL_PROGRAM_RULE_VIEW'
        },
      ]
    },
    {
      link: "/layout/online-registration",
      icon: "edit",
      menu: "Registration",
      data: { academic_year: '2024-2025' },
      task: 'SCHOOL_PROGRAM_REGISTRATION_VIEW'
    },
    {
      link: "/layout/registration-payment",
      icon: "credit-card",
      menu: "Payment",
      task: 'EXTRACURRICULAR_PARTICIPANT_MANAGE_PARTICIPANT',
      data: { academic_year: this.academic },
      expanded: false,
      children: [
        {
          link: "/layout/registration-payment",
          icon: "dollar-sign",
          menu: "Payment Log",
          data: { academic_year: this.academic },
          task: 'SCHOOL_PROGRAM_REGISTRATION_PAYMENT_VIEW'
        },
        {
          link: "/layout/payment-va",
          icon: "credit-card",
          menu: "Payment VA",
          data: { academic_year: this.academic },
          task: 'SCHOOL_PROGRAM_REGISTRATION_PAYMENT_VA_VIEW'
        },
      ]
    },
    {
      link: "/layout/activity-approver",
      icon: "check-circle",
      menu: "Approver",
      task: 'SCHOOL_PROGRAM_APPROVER_VIEW',
      data: { academic_year: this.academic },
      expanded: false,
      children: [
        {
          link: "/layout/registration-approver",
          icon: "user-check",
          menu: "Registration Approver",
          data: { academic_year: this.academic },
          task: 'SCHOOL_PROGRAM_REGISTRATION_APPROVER_VIEW'
        },
        {
          link: "/layout/activity-approver",
          icon: "check-square",
          menu: "Activity Approver",
          data: { academic_year: this.academic },
          task: 'SCHOOL_PROGRAM_ACTIVITY_APPROVER_VIEW'
        },
      ]
    },
    {
      link: "/layout/period-setup",
      icon: "tool",
      menu: "Setup",
      task: 'SCHOOL_PROGRAM_SETUP_VIEW',
      data: { academic_year: this.academic },
      expanded: false,
      children: [
        {
          link: "/layout/approver-setup",
          icon: "thumbs-up",
          menu: "Approver Setup",
          data: { academic_year: this.academic },
          task: 'SCHOOL_PROGRAM_APPROVER_SETUP_VIEW'
        },
        {
          link: "/layout/period-setup",
          icon: "calendar",
          menu: "Period Setup",
          data: { academic_year: this.academic },
          task: 'SCHOOL_PROGRAM_PERIOD_SETUP_VIEW'
        }
      ]
    },
    // {
    //   link: "/layout/registration-approver",
    //   icon: "check-circle",
    //   menu: "Registration Approver",
    //   data: { academic_year:  this.academic },
    //   task: 'SCHOOL_PROGRAM_REGISTRATION_APPROVER_VIEW'
    // },
    {
      link: "/layout/coach",
      icon: "users",
      menu: "Coach",
      data: { academic_year:  this.academic },
      task: 'SCHOOL_PROGRAM_COACH_VIEW'
    },
    {
      link: "/layout/attendance",
      icon: "clock",
      menu: "Attendance",
      data: { academic_year: this.academic },
      task: 'SCHOOL_PROGRAM_ATTENDANCE_VIEW'
    },
    {
      link: "/layout/scoring",
      icon: "hash",
      menu: "Scoring",
      data: { academic_year: this.academic },
      task: 'SCHOOL_PROGRAM_SCORING_VIEW'
    },
    {
      link: "/layout/payment-student",
      icon: "credit-card",
      menu: "Payment Details",
      data: { academic_year: this.academic },
      task: 'SCHOOL_PROGRAM_REGISTRATION_PAYMENT_STUDENT_VIEW'
    },
    {
      link: "/layout/report-unregistered",
      icon: "file-text",
      menu: "Report",
      task: 'SCHOOL_PROGRAM_REPORT_VIEW',
      data: { academic_year: this.academic },
      expanded: false,
      children: [
        {
          link: "/layout/report-unregistered",
          icon: "file-text",
          menu: "Report Unregistered",
          data: { academic_year: this.academic },
          task: 'SCHOOL_PROGRAM_REPORT_UNREGISTERED_VIEW'
        },
        {
          link: "/layout/report-registered",
          icon: "file-text",
          menu: "Report Registered",
          data: { academic_year: this.academic },
          task: 'SCHOOL_PROGRAM_REPORT_REGISTERED_VIEW'
        },
      ]
    },
    // {
    //   link: "/layout/activity-approver",
    //   icon: "check-square",
    //   menu: "Activity Approver",
    //   data: { academic_year: this.academic },
    //   task: 'SCHOOL_PROGRAM_ACTIVITY_APPROVER_VIEW'
    // },
    // {
    //   link: "/grid-list",
    //   icon: "file-text",
    //   menu: "Grid List",
    // },
    // {
    //   link: "/menu",
    //   icon: "menu",
    //   menu: "Menus",
    // },
    // {
    //   link: "/table",
    //   icon: "grid",
    //   menu: "Tables",
    // },
    // {
    //   link: "/expansion",
    //   icon: "divide-circle",
    //   menu: "Expansion Panel",
    // },
    // {
    //   link: "/chips",
    //   icon: "award",
    //   menu: "Chips",
    // },
    // {
    //   link: "/tabs",
    //   icon: "list",
    //   menu: "Tabs",
    // },
    // {
    //   link: "/progress",
    //   icon: "bar-chart-2",
    //   menu: "Progress Bar",
    // },
    // {
    //   link: "/toolbar",
    //   icon: "voicemail",
    //   menu: "Toolbar",
    // },
    // {
    //   link: "/progress-snipper",
    //   icon: "loader",
    //   menu: "Progress Snipper",
    // },
    // {
    //   link: "/tooltip",
    //   icon: "bell",
    //   menu: "Tooltip",
    // },
    // {
    //   link: "/snackbar",
    //   icon: "slack",
    //   menu: "Snackbar",
    // },
    // {
    //   link: "/slider",
    //   icon: "sliders",
    //   menu: "Slider",
    // },
    // {
    //   link: "/slide-toggle",
    //   icon: "layers",
    //   menu: "Slide Toggle",
    // },
  ]

  allTasks = [
    'SCHOOL_PROGRAM_DASHBOARD_VIEW',
    'SCHOOL_PROGRAM_VIEW',
    'SCHOOL_PROGRAM_ACTIVITY_VIEW',
    'SCHOOL_PROGRAM_RULE_VIEW',
    'SCHOOL_PROGRAM_REGISTRATION_VIEW',
    'SCHOOL_PROGRAM_REGISTRATION_PAYMENT_VIEW',
    'SCHOOL_PROGRAM_APPROVER_VIEW',
    'SCHOOL_PROGRAM_REGISTRATION_APPROVER_VIEW',
    'SCHOOL_PROGRAM_ACTIVITY_APPROVER_VIEW',
    'SCHOOL_PROGRAM_SETUP_VIEW',
    'SCHOOL_PROGRAM_APPROVER_SETUP_VIEW',
    'SCHOOL_PROGRAM_PERIOD_SETUP_VIEW',
    'SCHOOL_PROGRAM_COACH_VIEW',
    'SCHOOL_PROGRAM_ATTENDANCE_VIEW',
    'SCHOOL_PROGRAM_SCORING_VIEW'
  ];

  applyRouterOutlet = false;

  isMobile: boolean;

  token = null;
  tokenExpiredIn = null;
  tokenIsRefreshing = false;
  makingToken = 0;

  registrationPage: boolean;
  hideSidebar = false;
  parentVerifAlreadyChecked = false;

  survey_id = null;
  user_id = null;

  ngOnInit() {
    // @ts-ignore

    if (!localStorage.getItem('client.auth')) {
      window.location.reload();
      window.location.href = '/';
    }
    this.currentUser = this.auth.current().user;
    this.canSwitchAccount = this.authService.canSwitchAccount();
    this.isSwitchedAccount = this.authService.isSwitchedAccount();
    this.realAdminName = this.getRealAdminName();

    // this.isMobile = this.deviceService.isMobile() || this.deviceService.isTablet();

    const auth = this.authService.current();
    const tenant = this.settingService.getConfig('client.hostname');

    // auth-service sessions (renew-delete) are no longer used: the SSO session is checked by the API.
    // this.authService.renewDeleteSession({ action: 'renew' }).subscribe(
    //     (resDelete: Auth) => {
    //       // @ts-ignore
    //       if (resDelete['message'] === 'No data') {
    //         Swal.fire({
    //           allowOutsideClick: false,
    //           showConfirmButton: false,
    //           showCancelButton: false,
    //         });
    //         Swal.showLoading();
    //         setTimeout(() => {
    //           Swal.close();
    //         }, 1000);
    //         this.settingService.resetAll();
    //         window.location.reload();
    //       } else {
    //         this.authService.loadPermissions();
    //       }
    //     },
    //     error => {
    //       // this.toastr.error('[Session]', 'Connection Error');
    //       this.settingService.resetAll();
    //       window.location.reload();
    //     }
    // );
    this.authService.loadPermissions();

    // user has no corresponding task
    if (this.allTasks.some(task => this.auth.current().permissions[task])) {
      this.applyRouterOutlet = true;
    }

    this.router.events.pipe(
        filter(e => e instanceof ActivationEnd),
        buffer(this.router.events.pipe(filter(e => e instanceof NavigationEnd), debounceTime(0))),
        map((events: any[]) => events.reduce((acc, curr) => ({...acc, ...curr.snapshot.data, ...curr.snapshot._routerState}), {}))
    ).subscribe(res => {

      // console.log('router events:');

      // task checking
      this.clientPermissionAndDataFetch();
      this.buildBreadcrumb();

      // gtag('set', 'user_properties', {
      //   tenant: tenant,
      //   user_id: auth.user.uuid,
      //   roles: auth.roles,
      // });
      //
      // gtag('config', 'G-QSPC5HJJ9B', {
      //   'page_path': res['url']  
      // });

      if (!res['task']) {
        return;
      }

      this.authService.setMenuTask(res);
    });

    this.buildBreadcrumb();

    this.token = JSON.parse(<string>localStorage.getItem('client.auth'));

    this.filterSidebarMenu();

    // An SSO sign-in's token is not renewed: renewing it through Laravel's check drops its SSO
    // session id. SSO keeps the session alive on every request; when the token ends, a rejected
    // request signs in again.
    // @ts-ignore
    if (this.token['token']['expires_in'] && !this.authService.hasSsoSessionToken()) {
      // @ts-ignore
      this.tokenExpiredIn = new Date(this.token['token']['expires_in']);
      this.initializeClock();
    }
    // const idle = new IdleService(this.authService, this.settingService, this.toastr);
  }

  filterSidebarMenu() {
    const userPermissions = this.auth.current().permissions;
  
    this.sidebarMenu = this.sidebarMenu.filter(menu => {
      // Check if the user has the required permission for the menu item
      if (menu.task && !userPermissions[menu.task]) {
        return false;
      }
  
      // If the menu item has children, filter them as well
      if (menu.children) {
        menu.children = menu.children.filter(child => {
          return child.task ? userPermissions[child.task] : true;
        });
  
        // If no children remain after filtering, hide the parent menu
        if (menu.children.length === 0) {
          return false;
        }
      }
  
      return true;
    });
  }

  // onChangeAcademicYear(item: any, academic = null) {
  //   this.academic = academic ? academic : item.academic_year;
  //   this.router.navigate([], {
  //     queryParams: { academic_year: this.academic },
  //     queryParamsHandling: 'merge'
  //   }).then(() => {
  //   });

  // }
  // triggerAcademicYearChange() {
  //   console.log(this.academic);
  //   setTimeout(() => {
  //     this.onChangeAcademicYear(null, this.academic);
  //   }, 0);
  // }
  onLogOut() {
    this.auth.logout(false, true);
  }

  private getRealAdminName(): string | null {
    try {
      const real = JSON.parse(this.settingService.getConfig('client.authReal') as string);
      return real?.user?.fullname ?? null;
    } catch {
      return null;
    }
  }

  /** Opens the SSO account page (profile, password, devices) in a new tab. */
  async onOpenAccount(): Promise<void> {
    try {
      window.open(await this.ssoScript.getAccountUrl(), '_blank');
    } catch (err) {
      console.error(err);
      this.toastr.error('Gagal membuka halaman akun.', 'My Account');
    }
  }

  /** Switch and restore end like any SSO sign-in: the app reloads as the new account. */
  async onSwitchAccount(): Promise<void> {
    if (this.isSwitchingAccount) return;
    this.isSwitchingAccount = true;

    try {
      const result = await this.ssoScript.switchAccountWithPopup();
      await this.authService.completeSsoLogin(result, true);
    } catch (err: any) {
      this.isSwitchingAccount = false;
      this.showSwitchError(err, 'Gagal ganti akun. Coba lagi.');
    }
  }

  async onRestoreAccount(): Promise<void> {
    if (this.isRestoringAccount) return;
    this.isRestoringAccount = true;

    try {
      const result = await this.ssoScript.restoreOriginalAccountWithPopup();
      await this.authService.completeSsoLogin(result, true);
    } catch (err: any) {
      this.isRestoringAccount = false;
      this.showSwitchError(err, 'Gagal restore akun. Coba lagi.');
    }
  }

  private showSwitchError(err: any, fallback: string): void {
    // popup_closed = user sengaja tutup popup, tidak perlu error message
    if (err?.code === 'popup_closed' || err?.message === 'popup_closed') {
      return;
    }
    const message = err instanceof SsoLoginFailedError ? err.response?.message : err?.error?.message || err?.message;
    this.toastr.error(message || fallback, 'Switch Account');
  }

  initializeClock() {
    const getTimeRemaining = () => {
      // @ts-ignore
      const t = this.tokenExpiredIn - new Date().getTime();
      const seconds = Math.floor((t / 1000) % 60);
      const minutes = Math.floor((t / 1000 / 60) % 60);
      const hours = Math.floor((t / (1000 * 60 * 60)) % 24);
      const days = Math.floor(t / (1000 * 60 * 60 * 24));
      return {
        'total': t,
        'days': days,
        'hours': hours,
        'minutes': minutes,
        'seconds': seconds
      };
    };

    let interval: any;
    const updateClock = () => {
      const t = getTimeRemaining();
      if (t.hours < 1) {
        this.refreshToken();
        this.makingToken = 1;
        clearInterval(interval);
      }
      if (t.total <= 0) {
        clearInterval(setInterval(interval));
      }
    };
    if (this.makingToken === 0) {
      // updateClock();
      interval = setInterval(updateClock, 1000);
    }
  }

  refreshToken() {
    this.authService.check(true)
        .subscribe((response: any) => {
          if (response.error === 0) {
            this.settingService.setConfig('client.auth', JSON.stringify(response.result.data));
            this.settingService.setToken(response.result.data.token.access_token);
            // @ts-ignore
            this.tokenExpiredIn = new Date(response.result.data.token['expires_in']);
            console.log(`Token has been refreshed at ${new Date()}`);
            this.makingToken = 0;
            this.initializeClock();
          }
        });
  }

  clientPermissionAndDataFetch() {
    this.authService.check(true)
    .subscribe((response: any) => {
      if (response.error === 0) {
        this.settingService.setConfig('client.auth', JSON.stringify(response.result.data));
        this.settingService.setConfig('client.auth.permissions', JSON.stringify(response.result.data.permissions));
        this.settingService.setConfig('client.auth.permissions_roles', JSON.stringify(response.result.data.permissions_roles));
        this.auth.permissions = response.result.data.permissions;
        this.auth.permissionsRoles = response.result.data.permissions_roles;
      }
    });
  }

  buildBreadcrumb() {
    const url = this.router.url;
    const items = [];
    let current: any = NavigationItems
      .filter(item => url.startsWith(item.link))
      .sort((a, b) => b.link.length - a.link.length)[0];
    while (current) {
      items.unshift(current);
      if (!current.backLink || current.backLink === current.link) break;
      current = NavigationItems.find(item => item.link === current?.backLink);
    }
    this.breadcrumbItems = items;
  }
}
// trigger deploy